import { buyMore } from "@/api/add-to-cart";
import { ContentType } from "@/api/scode/scode_params";
import { useAddPurchasePanel } from "@/components/add-purchase-panel/hook/useAddPurchasePanel";
import PosterSwipe, { usePosterSwipePro } from "@/components/poster-swiper/pro";
import useCheckBind from "@/pages/new-person/hooks/useCheckBind";
import BindPhonePopup from "@/pages/personal/components/bind-phone-popup";
import PrdActivityPopup from "@/pages/promote/components/prd-activity-popup";
import { useCommonContext } from "@/utils/context/common";
import useNavigation from "@/utils/useNavigate";
import { ProductModel } from "@akc/biz-api";
import { ProductCard11SampleComp, VideoCardComp } from "@akc/mx-taro-h5-components";
import { useMount, useUnmount } from "ahooks";
import { InfiniteScroll, Popup, Toast } from "antd-mobile";
import Cookie from "js-cookie";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import styles from "./index.module.less";

export interface PrdVideoCardListCoreRef {
  // 拉起当前视频商品的转发海报
  showPoster: () => void;
}


interface Props {
  isBackground?: boolean; // 当前组件是否处于后台（进场进入后台需要暂停视频播放，进入前台再恢复播放）
  notifyFollowProduct?: { spuId?: string; isFollow?: boolean }; // 商品关注动态信息(用于商详返回，同步关注状态isFollow)
  activitySpuId?: string; // 初始商品的activitySpuId
  initMaterialNo?: string; // 初始商品的素材编号
  vcScene?: string; // 传给product/recommend接口的scene参数，用于指定场景,personVideoCard逛一逛推荐tab，videoCard_other_scene普通商品视频大卡，gyg_discover_page素材商品进的视频大卡
  showVideoBtmGuide?: boolean; // 是否展示视频底部引导文案“上滑看更多精彩视频”
  showVideoBottomBar?: boolean; // 是否展示视频底部工具栏
  hideForwardBtn?: boolean; // 是否隐藏转发按钮，默认false
  theme?: "dark" | "light"; // 视频主题色
  liveNo?: string; // 直播号
  trackDataCommon?: { 
    page_name?: string; 
    domain?: string;
    tab_name?: string; // 通用埋点字段
  };
  shareContentExtra?: { 
    page_name?: string;
    showVideoBtmGuide?: string; 
    showVideoBottomBar?: string;
    hideForwardBtn?: string;
    theme?: string;
    materialNo?: string;
  };
  posterParamCommon?:{sourceScene?: "PRODUCT_VIDEO_CARD" | "RECOMMEND_VIDEO_CARD"};
  /** 上下滚动 Swiper 高度,要带单位px */
  swiperHeight?: string;
  /** 在showVideoBottomBar=true的基础上，前 N 个底部漏出加购栏, 传undefined或null则默认前 3 个 */
  nextMarginLimit?: number;
  // 视频滑动变化
  onIndexChange?: (product: ProductModel, index: number, requestId: string) => void;
  // 商品sku选择确认
  onSkuSelectModelConfirm?: (product: ProductModel) => void;
  // 海报展示状态变化
  onPosterVisibleChange?: (visible: boolean) => void;
}


/**
 * 逛一逛，推荐tab的视频列表组件
 * 注意和/Users/mengxiang/codes/H5/mx-shop-micro/src/pages/product-detail/video-card-list/index.tsx的区别
 */
const PrdVideoCardListCore = forwardRef<PrdVideoCardListCoreRef, Props>(
  ({ 
    isBackground = false,
    notifyFollowProduct,
    activitySpuId,
    initMaterialNo,
    vcScene,
    showVideoBtmGuide = false,
    showVideoBottomBar = false,
    hideForwardBtn = false,
    theme = "dark",
    liveNo,
    trackDataCommon={
      page_name: "",
      domain: "",
      tab_name: "",
    },
    shareContentExtra,
    posterParamCommon={sourceScene: "PRODUCT_VIDEO_CARD"},
    swiperHeight,
    nextMarginLimit,
    onIndexChange,
    onSkuSelectModelConfirm,
    onPosterVisibleChange
  }, ref) => {

    
    const { userVisitInfo, track } = useCommonContext();
    const userIdRef = useRef<string>(Cookie.get("msUserCode") || "userId");

    const [currentProduct, setCurrentProduct] = useState<ProductModel | null>(null);
    // 商品、活动选择弹窗是否可见
    const [visible, setVisible] = useState(false);
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [searchRequestId, setSearchRequestId] = useState<string>("");


    const {
      bindPhonePopupVisible,
      setBindPhonePopupVisible,
      createBindPhoneTask,
      completeBindPhoneTask,
      toAuth,
    } = useCheckBind();

    // 加购弹窗相关
    const { openPanel, closePanel, AddPurchasePanelComponent } = useAddPurchasePanel();

    const posterControl = usePosterSwipePro({
      defaultUIConfig: {
        btnConfig: ["copy", "edit"],
      },
      defaultEnvContext: {
        sourceScene: posterParamCommon?.sourceScene,
      },
    });

    const { navigate2Product, navigate2Activity, navigate2Store, navigate2Cart } = useNavigation();



    // 关闭商品、活动选择弹窗
    const closeMoreProductDialog = () => {
      setVisible(false);
    };

    // 点击商品，跳商详
    const handleProductClick = (product: ProductModel) => {
      if (!product.activitySpuId) {
        return;
      }
      navigate2Product({ activitySpuId: product.activitySpuId, forceNormalProduct: true, liveNo});
    };
    // 点击更多商品，拉起商品、活动弹窗
    const handleMoreProductDialog = (product: ProductModel) => {
      setVisible(true);
    };

    // 点立即购买，拉起sku弹窗
    const handleBuyAction = async (product: ProductModel) => {
      if (!userVisitInfo?.isAuthorized) {
        toAuth();
        return;
      }
      await createBindPhoneTask();
      // 设置加购弹窗props
      openPanel({
        product,
        buttonType: 3,
      });
    };

    // 点击档期，跳档期详情页
    const handleActivityClick = (product: ProductModel) => {
      if (!product.activityId) {
        return;
      }
      navigate2Activity({ activityNo: product.activityId });
    };

    // 点击店铺，跳店铺详情页
    const handleShopClick = (product: ProductModel) => {
      if (!product.activitySimpleVO?.merchantShopCode) {
        return;
      }
      navigate2Store({
        merchantShopCode: product.activitySimpleVO?.merchantShopCode,
        shopCode: product.activitySimpleVO?.merchantShopCode,
        page_name: encodeURIComponent("商品视频"),
        productType: 1,
      });
    };

    // 点击加入购物车，拉起sku弹窗
    const handleAddCartAction = async (product: ProductModel) => {
      if (!userVisitInfo?.isAuthorized) {
        toAuth();
        return;
      }
  
      await createBindPhoneTask();
      openPanel({
        product,
        buttonType: 2,
      });
    };
  
    // 点击还想买
    const handleWant2BuyAction = async (product: ProductModel) => {
      if (!userVisitInfo?.isAuthorized) {
        toAuth();
        return;
      }
      await createBindPhoneTask();
      try {
        const response = await buyMore({
          activityId: product.activityId,
          activitySpuId: product.activitySpuId,
          selectSkuId: product.selectSkuId,
        });
        if (response) {
          Toast.show("你的购买意愿已通知商家");
        }
      } catch (error) {
        console.error(error);
      }
    };

    // 拉起指定商品的转发海报
    const showPosterInner = (product: ProductModel|null,{share_type,isVideoCardPrd}: {share_type: string,isVideoCardPrd: boolean}) => {
      if(!product){
        return;
      }
      
      if (!product.activitySpuId) {
        return;
      }
      let contentExtra = {
        hasVideo: product.productVideoCardVO?.type==="VIDEO",
        materialNo: product?.productVideoCardVO?.materialNo || "",
      };
      // 只有视频大卡商品需要带扩展参数，跳视频大卡页，弹窗商品跳的是商详
      if(isVideoCardPrd){
        contentExtra = {
          ...(shareContentExtra || {}),
          ...contentExtra
        };
      }
      posterControl.open({
        bizInfo: {
          contentId: product.activitySpuId,
          contentType: ContentType.spuProduct,
          contentDetailId: product.selectSkuId,
          contentExtra,
        },
        contentContext: {
          contentExtra,
        },
      });
      if (track && track.track) {
        track.track("$share", {
          domain: trackDataCommon.domain,
          material_id: product?.materialRelationProductInfoVO?.materialNo,
          page_name: trackDataCommon.page_name,
          share_channel: "微信好友/群",
          share_name: "转发卡片",
          share_num: "1",
          shaer_setting: "H5",
          is_default: false,
          share_t: new Date().valueOf(),
          share_type,
        });
      }
    };

    // 点击视频商品转发，拉起指定商品的转发海报
    const handleForwardActionOfVideoPrd = (product: ProductModel) => {
      showPosterInner(product,{share_type:"商品",isVideoCardPrd:true});
    };
    
    const onCollectPrdAction = (product: ProductModel,newFollowInfo:{newIsFollow: boolean}) => {
      const {newIsFollow} = newFollowInfo || {};
      sessionStorage.setItem("PRD_VIDEO_CARD_FELLOW_CHANGE", JSON.stringify({
        activitySpuId:product.activitySpuId,
        newFollowInfo:{
          newIsFollow,
          // newFollowNum ，视频大卡接口没有返回 followNum,所以newFollowNum的值是错的，不能用
        }
      }));
    };
    // 点击弹窗商品转发，拉起指定商品的转发海报
    const handleForwardActionOfPopPrd = (product: ProductModel) => {
      showPosterInner(product,{share_type:"商品",isVideoCardPrd:false});
    };


    // 滑动视频，
    const handleIndexChange = (product: ProductModel, index: number, requestId: string) => {
      if (!product) {
        return;
      }
      setCurrentProduct(product);
      setCurrentIndex(index);
      if (requestId) {
        setSearchRequestId(requestId);
      }
      onIndexChange?.(product, index, requestId);
    };




    useEffect(() => {
      onPosterVisibleChange?.(posterControl.visible);
    }, [posterControl.visible]);

    useImperativeHandle(ref, () => ({
      showPoster:() => {
        showPosterInner(currentProduct,{share_type:"种草视频",isVideoCardPrd:true});
      },
    }));

    useMount(() => {
      if (track && track.track) {
        track.track("$pageview", {
          page_name: trackDataCommon.page_name,
          domain: trackDataCommon.domain,
          tab_name: trackDataCommon.tab_name,
        });
      }
    });

    return (
      <div>
        {/* 视频大卡列表 */}
        <VideoCardComp
          activitySpuId={activitySpuId}
          materialNo={initMaterialNo}
          swiperHeight={swiperHeight}
          titleFrontLocationCode="H5_ProductList_styleNo1_titleFront"
          titleBottomLocationCode="H5_ProductList_styleNo1_titleDown"
          onProductAction={handleProductClick}
          onMoreProductDialog={handleMoreProductDialog}
          onB2RShopAction={handleShopClick}
          onActivityAction={handleActivityClick}
          onBuyAction={handleBuyAction}
          onAddCartAction={handleAddCartAction}
          onForwardAction={handleForwardActionOfVideoPrd}
          onCollectAction={onCollectPrdAction}
          onWant2BuyAction={handleWant2BuyAction}
          onIndexChange={handleIndexChange}
          vcScene={vcScene}
          notifyFollowProduct={notifyFollowProduct}
          isBackground={isBackground}
          nextMarginLimit={nextMarginLimit}
          theme={theme}
          showGuide={showVideoBtmGuide}
          showBottomBar={showVideoBottomBar}
          hideForwardBtn={hideForwardBtn}
          trackData={{
            page_name: trackDataCommon.page_name,
            domain: trackDataCommon.domain,
          }}
          userId={userIdRef.current}
        />

        {/* 多规弹窗 */}
        {AddPurchasePanelComponent}

        {/* 分享海报弹窗 */}
        <PosterSwipe {...posterControl.props} />


      {/* 商品选择弹窗 */}
      {visible && <PrdActivityPopup
        queryBy="PRODUCT"
        activitySpuId={currentProduct?.activitySpuId}
        materialNo={currentProduct?.materialRelationProductInfoVO?.materialNo || ""}
        onClose={closeMoreProductDialog}
        onProductClick={handleProductClick}
        onBuyAction={handleBuyAction}
        onAddCartAction={handleAddCartAction}
        onForwardaPrdAction={handleForwardActionOfPopPrd}
      />}

        {/* 绑定手机号弹窗 */}
        <BindPhonePopup
          visible={bindPhonePopupVisible}
          setVisible={setBindPhonePopupVisible}
          onFinish={() => {
            completeBindPhoneTask();
          }}
        />
      </div>
    );
  },
);

export default PrdVideoCardListCore;
